import { type FC } from "react";
import { CheckCircle2, Circle, MinusCircle } from "lucide-react";

import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";
import type { Database } from "@/shared/types/database.types";

type EstateDocumentRow = Database["public"]["Tables"]["estate_documents"]["Row"];

type ChecklistDocument = EstateDocumentRow & {
  name?: string;
  file_size?: number;
};

interface ChecklistItem {
  label: string;
  documentTypes: string[];
  notApplicableLabel?: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  { label: "Death certificate", documentTypes: ["death_certificate"] },
  { label: "Will", documentTypes: ["will"], notApplicableLabel: "No will" },
  {
    label: "Trust",
    documentTypes: ["trust", "certification_of_trust"],
    notApplicableLabel: "No trust",
  },
  { label: "User ID", documentTypes: ["identification_card"] },
  {
    label: "Letters Testamentary / LOA",
    documentTypes: ["letters_testamentary", "letters_administration"],
  },
  { label: "EIN confirmation", documentTypes: ["ein_confirmation"] },
];

interface KeyDocumentsChecklistProps {
  documents: ChecklistDocument[];
  naDocumentTypes: Set<string> | undefined;
  onDocumentClick: (doc: ChecklistDocument) => void;
  onViewAllDocuments: () => void;
}

export const KeyDocumentsChecklist: FC<KeyDocumentsChecklistProps> = ({
  documents,
  naDocumentTypes,
  onDocumentClick,
  onViewAllDocuments,
}) => {
  const rows = CHECKLIST_ITEMS.map((item) => {
    const doc = documents.find(
      (d) => d.document_type && item.documentTypes.includes(d.document_type.toLowerCase()),
    );
    const notApplicable =
      !doc && !!naDocumentTypes && item.documentTypes.some((type) => naDocumentTypes.has(type));
    return { ...item, doc, notApplicable };
  });

  const presentCount = rows.filter((row) => row.doc).length;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-primary">Key Documents</h3>
          <span className="text-sm text-secondary">
            {presentCount} of {CHECKLIST_ITEMS.length}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          {rows.map((row) => {
            const isPresent = !!row.doc;
            return (
              <button
                key={row.label}
                type="button"
                disabled={!isPresent}
                onClick={() => row.doc && onDocumentClick(row.doc)}
                className={cn(
                  "flex items-center gap-3 -mx-2 px-2 py-2 rounded-sm text-left w-full",
                  isPresent ? "cursor-pointer hover:bg-subtle transition-colors" : "cursor-default",
                )}
              >
                {isPresent ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                ) : row.notApplicable ? (
                  <MinusCircle className="h-5 w-5 text-stone-300 flex-shrink-0" />
                ) : (
                  <Circle className="h-5 w-5 text-stone-300 flex-shrink-0" />
                )}
                <span
                  className={cn(
                    "text-sm flex-1",
                    isPresent ? "text-primary" : "text-tertiary",
                    row.notApplicable && "line-through",
                  )}
                >
                  {row.label}
                </span>
                {row.notApplicable && (
                  <span className="text-2xs text-tertiary flex-shrink-0">
                    {row.notApplicableLabel ?? "Not applicable"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="text-center mt-4">
          <Button
            variant="link"
            size="sm"
            onClick={onViewAllDocuments}
            className="text-brand text-sm"
          >
            View all {documents.length} documents →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
